import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldOff, UserPlus, RefreshCw, Loader2, Mail, Clock } from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

interface AdminRow {
  id: string;
  email: string;
  user_type: string;
  status: string;
  created_at?: string;
}
interface AllowRow {
  email: string;
  note?: string | null;
  created_at?: string;
}

/**
 * Platform administrator management.
 *
 * Admin rights are NEVER written from the browser — every change here goes
 * through the `admin-set-role` edge function, which re-checks server-side that
 * the caller really is an administrator before touching mkt_profiles.is_admin.
 *
 * An email can also be PRE-AUTHORISED: if nobody has registered with it yet the
 * address is stored in mkt_admin_emails and a database trigger grants
 * administrator rights automatically the moment that account is created.
 */
const AdminAdministrators: React.FC = () => {
  const { lang } = useI18n();
  const { profile, refreshProfile } = useAuth();
  const es = lang === 'es';

  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [allowlist, setAllowlist] = useState<AllowRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke('admin-set-role', {
      body: { action: 'list-admins' },
    });
    if (error || data?.error) {
      setErr(data?.error || error?.message || 'Failed to load administrators');
    } else {
      setAdmins(data.admins || []);
      setAllowlist(data.allowlist || []);
      setErr('');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const call = async (body: Record<string, unknown>, key: string) => {
    setBusy(key);
    setMsg('');
    setErr('');
    const { data, error } = await supabase.functions.invoke('admin-set-role', { body });
    setBusy('');
    if (error || data?.error) {
      setErr(data?.error || error?.message || 'Request failed');
      return null;
    }
    setMsg(
      data.message ||
        (data.pending
          ? es
            ? 'Correo pre-autorizado. Recibirá acceso de administrador al registrarse.'
            : 'Email pre-authorised. It will receive administrator access at registration.'
          : es
          ? 'Permisos actualizados.'
          : 'Permissions updated.'),
    );
    await load();
    await refreshProfile();
    return data;
  };

  const grant = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      setErr(es ? 'Escriba un correo válido.' : 'Enter a valid email address.');
      return;
    }
    call({ action: 'set-admin', email: value, isAdmin: true }, 'grant').then((ok) => {
      if (ok) setEmail('');
    });
  };

  const revoke = (row: AdminRow) => {
    if (row.id === profile?.id) {
      setErr(es ? 'No puede quitarse su propio acceso.' : 'You cannot remove your own access.');
      return;
    }
    call({ action: 'set-admin', userId: row.id, email: row.email, isAdmin: false }, row.id);
  };

  const removePending = (row: AllowRow) =>
    call({ action: 'set-admin', email: row.email, isAdmin: false }, row.email);

  const pending = allowlist.filter(
    (a) => !admins.some((ad) => ad.email.toLowerCase() === a.email.toLowerCase()),
  );

  return (
    <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            {es ? 'Administradores de la plataforma' : 'Platform administrators'}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            {es
              ? 'Estas cuentas pueden abrir /admin y todas sus pestañas. Los permisos se cambian en el servidor: el navegador nunca puede otorgarse acceso.'
              : 'These accounts can open /admin and every tab inside it. Permissions are changed server-side — the browser can never grant itself access.'}
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {err && (
        <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{err}</p>
      )}
      {msg && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{msg}</p>
      )}

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">{es ? 'Correo' : 'Email'}</th>
              <th className="px-4 py-3">{es ? 'Tipo de cuenta' : 'Account type'}</th>
              <th className="px-4 py-3">{es ? 'Acceso' : 'Access'}</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : (
              admins.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">
                    {a.email}
                    {a.id === profile?.id && (
                      <span className="ml-2 rounded-full bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-700">
                        {es ? 'usted' : 'you'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{a.user_type}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                      {es ? 'Administrador total' : 'Full administrator'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => revoke(a)}
                      disabled={busy === a.id || a.id === profile?.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <ShieldOff className="h-3.5 w-3.5" /> {es ? 'Quitar' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pending.length > 0 && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
            <Clock className="h-4 w-4" />
            {es ? 'Pre-autorizados (aún sin cuenta)' : 'Pre-authorised (no account yet)'}
          </h3>
          <ul className="mt-3 space-y-2">
            {pending.map((p) => (
              <li key={p.email} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="inline-flex items-center gap-2 font-semibold text-amber-900">
                  <Mail className="h-4 w-4" /> {p.email}
                </span>
                <button
                  onClick={() => removePending(p)}
                  disabled={busy === p.email}
                  className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 disabled:opacity-40"
                >
                  {es ? 'Quitar' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-amber-800">
            {es
              ? 'Estos correos reciben acceso de administrador automáticamente en el momento en que se registran.'
              : 'These addresses receive administrator access automatically the moment they register.'}
          </p>
        </div>
      )}

      <form onSubmit={grant} className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-500">
          {es ? 'Dar acceso de administrador' : 'Grant administrator access'}
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={es ? 'correo@ejemplo.com' : 'name@example.com'}
            className="min-w-[240px] flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm"
          />
          <button
            type="submit"
            disabled={busy === 'grant'}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {busy === 'grant' ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            {es ? 'Dar acceso' : 'Grant access'}
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {es
            ? 'Si la cuenta ya existe se actualiza al instante. Si todavía no existe, el correo queda pre-autorizado.'
            : 'If the account already exists it is updated instantly. If it does not exist yet, the email is pre-authorised.'}
        </p>
      </form>
    </section>
  );
};

export default AdminAdministrators;
